<template>
  <v-card class="ma-1">
    <v-toolbar>
      <v-btn
        class="mr-3"
        title="Back to Standardizer Home"
        @click="$router.push('/stdizer')"
        v-b-tooltip
        aria-label="Back to Standardizer Home"
      >
        <v-icon>mdi-arrow-up</v-icon>
      </v-btn>

      <v-text-field
        v-model="search"
        append-icon="mdi-magnify"
        hide-details
        label="Search in any field"
        single-line
      />

      <v-spacer />

      <v-btn-toggle dense>
        <FileUploadDialog
          button-title="mdi-file-upload-outline"
          @uploaded="onFileUploaded"
          label="JSON file"
          title="Create from JSON file(s)"
          :preflight-url="`${STDIZER_URL}/groups/preflight`"
          :upload-url="`${STDIZER_URL}/groups`"
          show-replace
          multiple
          max-width="800"
        >
          <template #filesInfo="{ filesHeaders, replace }">
            <b-table
              v-if="filesHeaders && filesHeaders.length"
              :fields="[
                replace ? [] : [{ key: 'uniqueName', label: 'New Group' }],
                ...[
                  { key: 'name', label: 'Existing Group' },
                  { key: 'frozen', label: 'Frozen' },
                  { key: 'operationsNumber', label: 'Operations' },
                ],
              ]"
              :items="filesHeaders"
            >
              <template #cell(frozen)="{ value }">
                <v-icon
                  v-if="value"
                  title="Frozen"
                  :style="{ color: replace ? 'red' : 'deepskyblue' }"
                >
                  mdi-snowflake
                </v-icon>
              </template>
            </b-table>
          </template>
        </FileUploadDialog>

        <PromptDialog
          v-model="visibleCopy"
          :visible="false"
          :text="editingItem ? editingItem.value : null"
          title="Do you want to copy?"
          @confirmed="copyItem"
        />

        <DeleteDialog
          v-model="visibleDelete"
          :items="selected"
          :visible="false"
          button-title="mdi-delete"
          object-type="groups"
          title="Delete selected actions groups"
          @click="deleteItems(selected)"
          @itemDeleted="removeItem"
          :api-url="STDIZER_URL"
        />

        <OperationEditor
          v-model="visibleEdit"
          :item="editingItem"
          @onSave="itemUpdated"
          :on-button-activate="resetEditingItem"
          button-title="mdi-playlist-plus"
          title="Add"
          max-width="600"
          global
        />
      </v-btn-toggle>
    </v-toolbar>

    <v-data-table
      :footer-props="{
        showFirstLastPage: true,
        itemsPerPageOptions: [10, 30, 50, -1],
      }"
      :hide-default-footer="items.length <= 10"
      :headers="headers"
      :items="items"
      :search="search"
      :items-per-page="30"
      item-key="key"
    >
      <template #[`item.type`]="{ item }">
        <span
          :style="{
            'font-weight': item.type === 'Workflow' ? 'bolder' : 'normal',
          }"
        >
          {{ item.type }}
        </span>
      </template>

      <template #[`item.name`]="{ item }">
        <b-link
          :to="`/stdizer/groups/${encodeURIComponent(item.name)}`"
          :style="{
            'font-weight': item.type === 'Workflow' ? 'bolder' : 'normal',
          }"
        >
          {{ item.name }}
        </b-link>
      </template>

      <template #[`item.operationsNumber`]="{ item }">
        <v-chip small>{{ item.operationsNumber }}</v-chip>
      </template>

      <template #[`item.access`]="{ item }">
        <v-chip small>{{ item.acl ? item.acl.access : 'PUBLIC' }}</v-chip>
      </template>

      <template #[`item.status`]="{ item }">
        <v-icon
          v-if="item.frozen"
          title="Frozen"
          style="color: deepskyblue"
          v-b-tooltip
        >
          mdi-snowflake
        </v-icon>
        <v-icon
          v-if="item.invalid"
          :title="item.invalidMessage"
          color="red"
          v-b-tooltip
        >
          mdi-alert-circle-outline
        </v-icon>
      </template>

      <template #[`item.actions`]="{ item }">
        <v-icon
          v-if="!item.frozen"
          title="Edit"
          @click="editItem(item)"
          class="ma-1"
        >
          mdi-playlist-edit
        </v-icon>
        <v-icon
          title="Copy"
          @click="confirmCopyItem(item)"
          class="ma-1"
        >
          mdi-content-copy
        </v-icon>
        <v-icon
          v-if="!item.frozen"
          title="Delete"
          @click="deleteItems([item])"
          class="ma-1"
        >
          mdi-delete
        </v-icon>
      </template>
    </v-data-table>
  </v-card>
</template>

<script>
import { STDIZER_URL } from '@/main'
import DeleteDialog from '@/components/dialogs/DeleteDialog.vue'
import PromptDialog from '@/components/dialogs/PromptDialog.vue'
import OperationEditor from '@/components/stdizer/OperationEditor'
import FileUploadDialog from '@/components/dialogs/FileUploadDialog.vue'
import OperationsGroupBase from '@/components/stdizer/OperationsGroupBase'

export default {
  mixins: [OperationsGroupBase],
  components: { PromptDialog, DeleteDialog, OperationEditor, FileUploadDialog },

  data: () => ({
    headers: [
      { text: 'Type', value: 'type' },
      { text: 'Name', value: 'name' },
      { text: 'Status', value: 'status', filterable: false },
      { text: 'Title', value: 'title' },
      { text: 'Description', value: 'description' },
      { text: 'Operations #', value: 'operationsNumber', filterable: false },
      { text: 'Access', value: 'access', filterable: false },
      {
        text: 'Actions',
        value: 'actions',
        sortable: false,
        filterable: false,
        align: 'end',
      },
    ],
  }),

  methods: {
    removeItem(item) {
      this.items.splice(this.items.indexOf(item), 1)
    },
    copyItem() {
      this.$axios.post(`${STDIZER_URL}/groups`, this.editingItem).then((response) => {
        response.data.key = this.maxKey++
        this.items.push(response.data)
      })
    },
    updateDatabase(group) {
      if (this.editingItem)
        this.$axios.put(`${STDIZER_URL}/groups/${this.editingItem.id}`, this.editingItem).then(() => {
          // this.$router.go(0);
        })
      else
        this.$axios.post(`${STDIZER_URL}/groups`, group).then(() => {
          //
        })
    },
    onFileUploaded() {
      this.getData()
    },
    getData() {
      this.maxKey = 0
      this.$axios.get(`${STDIZER_URL}/groups`).then((response) => {
        response.data.forEach((s) => (s.key = this.maxKey++))
        this.items = response.data
      })
    },
  },
}
</script>
